import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiAward } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  AwardMethodDto,
  BidV2Status,
  EvaluationModelDto,
  type EvalBidDto,
  type EvaluationDto,
  type EventValueDto,
} from "../../../../autogen/bff-api";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";

const formatPrice = (v?: EventValueDto | null): string =>
  v?.value != null ? `${v.value.toLocaleString("nb-NO")} ${v.currency}` : "–";

export const isRejectedBid = (bid: EvalBidDto): boolean => bid.status === BidV2Status.Rejected;

export const isUnrankedBid = (bid: EvalBidDto): boolean => !isRejectedBid(bid) && bid.rank <= 0;

export const hasAnyScore = (bid: EvalBidDto): boolean => bid.cells.some((cell) => cell.points != null);

export const ranksByPriceOnly = (evaluation: Pick<EvaluationDto, "model" | "awardMethod">): boolean =>
  evaluation.model === EvaluationModelDto.Price || evaluation.awardMethod === AwardMethodDto.LowestPrice;

type AwardBidsTableProps = {
  evaluation: EvaluationDto;
  selectedBidIds: string[];
} & ({ mode: "select"; onToggle: (bidId: string) => void } | { mode: "confirmed"; onToggle?: never });

const sortOrder = (bid: EvalBidDto): number => (isRejectedBid(bid) ? 2 : isUnrankedBid(bid) ? 1 : 0);

export const sortAwardBids = (bids: EvalBidDto[]) =>
  [...bids].sort((a, b) => sortOrder(a) - sortOrder(b) || a.rank - b.rank);

export const AwardBidsTable = ({ evaluation, mode, selectedBidIds, onToggle }: AwardBidsTableProps) => {
  const { t } = useTranslation();
  const priceOnly = ranksByPriceOnly(evaluation);
  const bids = sortAwardBids(evaluation.bids);

  const table = (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th w="1px" whiteSpace="nowrap">
            {mode === "select" ? t("Select") : t("Winner")}
          </Th>
          <Th w="1px" textAlign="center">
            #
          </Th>
          <Th w="100%">{t("Supplier")}</Th>
          <Th w="1px" whiteSpace="nowrap" isNumeric>
            {priceOnly ? t("Bid sum") : t("Total score")}
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {bids.map((bid) => {
          const isRejected = isRejectedBid(bid);
          const isUnranked = isUnrankedBid(bid);
          const isWinner = selectedBidIds.includes(bid.bidId);
          return (
            <Tr
              key={bid.bidId}
              bg={isRejected ? "red.50" : isWinner ? "green.50" : undefined}
            >
              <Td
                w="1px"
                borderLeft={isRejected || isWinner ? "3px solid" : undefined}
                borderLeftColor={
                  isRejected ? "red.300" : isWinner ? "green.400" : undefined
                }
              >
                {mode === "select" ? (
                  <Checkbox
                    isChecked={isWinner}
                    onChange={() => onToggle(bid.bidId)}
                    colorScheme="teal"
                    isDisabled={isRejected}
                    aria-label={t("Award to {{supplier}}", { supplier: bid.supplierName || t("Unknown") }) ?? undefined}
                  />
                ) : isWinner ? (
                  <Box
                    as={FiAward}
                    color="green.500"
                    boxSize="5"
                    data-testid="award-winner-icon"
                    aria-label={t("Winner") ?? undefined}
                  />
                ) : null}
              </Td>
              <Td w="1px" textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  display="inline-flex"
                  boxSize="7"
                  rounded="full"
                  bg={
                    isRejected
                      ? "red.100"
                      : isWinner
                        ? "smPrimary"
                        : "smBackgroundSecondary"
                  }
                  color={
                    isRejected ? "red.700" : isWinner ? "white" : "smMuted"
                  }
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {isRejected || isUnranked ? "–" : bid.rank}
                </Flex>
              </Td>
              <Td w="100%">
                <Flex align="center" gap="2" wrap="wrap">
                  <Text fontWeight="semibold" color="smText">
                    {bid.supplierName || t("Unknown")}
                  </Text>
                  {isRejected && (
                    <Badge colorScheme="red">{t("Rejected")}</Badge>
                  )}
                  {isUnranked && <Badge colorScheme="gray">{t("Not ranked")}</Badge>}
                  {!isRejected && (
                    <MinimumRequirementOutcomeBadge
                      minimumRequirements={bid.minimumRequirements}
                    />
                  )}
                </Flex>
                {!priceOnly && (
                  <Text fontSize="sm" color="smMuted">
                    {formatPrice(bid.effectivePrice ?? bid.price)}
                  </Text>
                )}
              </Td>
              {priceOnly ? (
                <Td w="1px" isNumeric whiteSpace="nowrap" fontWeight="medium" color="smText">
                  {formatPrice(bid.effectivePrice ?? bid.price)}
                </Td>
              ) : (
                <Td
                  w="1px"
                  isNumeric
                  whiteSpace="nowrap"
                  fontWeight="bold"
                  fontFamily="mono"
                  color={isWinner ? "smPrimary" : "smText"}
                >
                  {hasAnyScore(bid) ? bid.weightedTotal.toFixed(1) : "–"}
                </Td>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <Box
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      overflow="hidden"
    >
      <TableContainer>{table}</TableContainer>
    </Box>
  );
};
